import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GripVertical, Loader2, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { API } from "@/context/AuthContext";

const EMPTY = { name: "", technology: "", capacity: "", notes: "", image_url: "", sort_order: 100, is_active: true };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function MachinesManager() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/machines/all`, { withCredentials: true });
      setMachines(data);
    } catch { toast.error("Maschinen konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const nextOrder = machines.length ? Math.max(...machines.map(m => m.sort_order || 0)) + 10 : 10;
    setEditing("new"); setForm({ ...EMPTY, sort_order: nextOrder });
  };
  const openEdit = (m) => { setEditing(m.id); setForm({ name: m.name, technology: m.technology, capacity: m.capacity || "", notes: m.notes || "", image_url: m.image_url || "", sort_order: m.sort_order ?? 100, is_active: m.is_active }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/machines/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image_url: data.url }));
      toast.success("Bild hochgeladen");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.name.trim() || !form.technology.trim()) {
      toast.error("Name und Technologie sind Pflicht"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 100 };
      if (editing === "new") await axios.post(`${API}/machines`, payload, { withCredentials: true });
      else await axios.patch(`${API}/machines/${editing}`, payload, { withCredentials: true });
      toast.success("Maschine gespeichert"); close(); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Maschine "${name}" wirklich löschen?`)) return;
    try { await axios.delete(`${API}/machines/${id}`, { withCredentials: true }); toast.success("Maschine gelöscht"); await load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const toggleActive = async (m) => {
    try { await axios.patch(`${API}/machines/${m.id}`, { ...m, is_active: !m.is_active }, { withCredentials: true }); await load(); }
    catch { toast.error("Status konnte nicht geändert werden"); }
  };

  const input = "w-full h-11 px-4 rounded-md border border-[#D9E0E6] bg-white text-sm text-[#1d1d1b] focus:outline-none focus:ring-2 focus:ring-[#00AAA0] focus:border-[#00AAA0]";
  const label = "block font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b] mb-2";

  return (
    <div data-testid="machines-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[#1d1d1b]">Maschinenpark</h2>
          <p className="text-sm text-[#1d1d1b]/70 mt-1">Aktive Maschinen werden öffentlich auf der Startseite angezeigt (Reihenfolge über <em>sort_order</em>).</p>
        </div>
        <button onClick={openNew} data-testid="machine-new-btn"
          className="flex items-center gap-2 px-5 h-10 rounded-md bg-[#2C57D2] hover:bg-[#00AAA0] text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors">
          <Plus size={14} /> Neue Maschine
        </button>
      </div>

      <div className="border border-[#E3E8EC] rounded-lg overflow-hidden bg-white" data-testid="machines-table">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-[#F7F9FA] border-b border-[#E3E8EC]">
          <span className="col-span-1 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]">#</span>
          <span className="col-span-2 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]">Bild</span>
          <span className="col-span-4 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]">Name</span>
          <span className="col-span-3 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]">Technologie</span>
          <span className="col-span-1 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b]">Status</span>
          <span className="col-span-1 font-tech text-[10px] tracking-[0.22em] uppercase text-[#1d1d1b] text-right">Aktion</span>
        </div>
        {loading ? (
          <div className="px-6 py-16 text-center text-[#1d1d1b]"><Loader2 className="animate-spin inline mr-2" size={16} /> Lade…</div>
        ) : machines.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-[#1d1d1b]">Noch keine Maschinen angelegt.</div>
        ) : machines.map((m, i) => (
          <div key={m.id} className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 items-center ${i < machines.length-1 ? "border-b border-[#EEF1F4]" : ""}`} data-testid={`machine-row-${m.id}`}>
            <div className="md:col-span-1 flex items-center gap-2 font-tech text-xs text-[#1d1d1b]/70">
              <GripVertical size={14} className="text-[#B9C2CA]" />
              {m.sort_order}
            </div>
            <div className="md:col-span-2">
              {m.image_url ? (
                <img src={resolveImg(m.image_url)} alt={m.name} className="w-24 h-16 object-cover rounded-md border border-[#E3E8EC]" />
              ) : (
                <div className="w-24 h-16 rounded-md border border-dashed border-[#D9E0E6] flex items-center justify-center text-[10px] text-[#1d1d1b]/50">Kein Bild</div>
              )}
            </div>
            <div className="md:col-span-4 font-medium text-[#1d1d1b]">{m.name}</div>
            <div className="md:col-span-3 font-tech text-xs text-[#2C57D2]">{m.technology}</div>
            <button onClick={() => toggleActive(m)} data-testid={`machine-toggle-${m.id}`}
              className={`md:col-span-1 inline-flex px-2.5 py-1 rounded-full border font-tech text-[10px] tracking-[0.15em] uppercase self-start md:self-center transition-colors ${m.is_active ? "border-[#00AAA0] text-[#00AAA0] bg-[#00AAA0]/5" : "border-[#E3E8EC] text-[#1d1d1b]/60"}`}>
              {m.is_active ? "Aktiv" : "Inaktiv"}
            </button>
            <div className="md:col-span-1 flex items-center gap-2 md:justify-end">
              <button onClick={() => openEdit(m)} data-testid={`machine-edit-${m.id}`} className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:border-[#00AAA0] hover:text-[#00AAA0] transition-colors"><Pencil size={14}/></button>
              <button onClick={() => remove(m.id, m.name)} data-testid={`machine-delete-${m.id}`} className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E3E8EC] text-[#1d1d1b] hover:border-[#EF4444] hover:text-[#EF4444] transition-colors"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="machine-form-modal">
          <div className="bg-white rounded-lg w-full max-w-3xl my-8 border border-[#E3E8EC]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E3E8EC]">
              <h3 className="font-bold text-[#1d1d1b]">{editing === "new" ? "Neue Maschine anlegen" : "Maschine bearbeiten"}</h3>
              <button onClick={close} className="w-9 h-9 rounded-md border border-[#E3E8EC] flex items-center justify-center text-[#1d1d1b] hover:text-[#EF4444] hover:border-[#EF4444]" data-testid="machine-form-close"><X size={14}/></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={label}>Name *</label>
                  <input className={input} value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="z. B. Faserlaser 6 kW" data-testid="machine-form-name" />
                </div>
                <div>
                  <label className={label}>Technologie *</label>
                  <input className={input} value={form.technology} onChange={e => setForm(f => ({...f, technology: e.target.value}))} placeholder="z. B. Laserschneiden · 6 kW" data-testid="machine-form-tech" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={label}>Kapazität / Arbeitsbereich</label>
                  <input className={input} value={form.capacity} onChange={e => setForm(f => ({...f, capacity: e.target.value}))} placeholder="z. B. 3.000 × 1.500 mm" data-testid="machine-form-cap" />
                </div>
                <div>
                  <label className={label}>Reihenfolge (kleiner = weiter oben)</label>
                  <input type="number" className={input} value={form.sort_order} onChange={e => setForm(f => ({...f, sort_order: e.target.value}))} data-testid="machine-form-sort" />
                </div>
              </div>
              <div>
                <label className={label}>Hinweise / Bemerkungen</label>
                <textarea className={`${input} h-24 py-3 resize-y`} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="z. B. Materialstärken, Einsatzgebiete …" data-testid="machine-form-notes" />
              </div>
              <div>
                <label className={label}>Maschinenbild</label>
                <div className="flex items-center gap-4">
                  {form.image_url ? (
                    <div className="relative w-40 h-24 rounded-md overflow-hidden border border-[#E3E8EC]">
                      <img src={resolveImg(form.image_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-40 h-24 rounded-md border border-dashed border-[#D9E0E6] flex items-center justify-center text-[#1d1d1b]/50 text-xs">Kein Bild</div>
                  )}
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={e => upload(e.target.files?.[0])} hidden data-testid="machine-form-file" />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="flex items-center gap-2 px-4 h-10 rounded-md border border-[#2C57D2] text-[#2C57D2] hover:bg-[#2C57D2] hover:text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-60" data-testid="machine-form-upload-btn">
                    {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Bild wählen
                  </button>
                  {form.image_url && <button type="button" onClick={() => setForm(f => ({...f, image_url: ""}))} className="text-xs text-[#1d1d1b]/60 hover:text-[#EF4444]">entfernen</button>}
                </div>
                <p className="mt-2 text-xs text-[#1d1d1b]/60">JPG / PNG / WEBP / GIF – max. 8 MB. Empfohlen: 1200 × 800 px (Querformat).</p>
              </div>
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({...f, is_active: e.target.checked}))} className="w-4 h-4 accent-[#00AAA0]" data-testid="machine-form-active" />
                <span className="text-sm text-[#1d1d1b]">Maschine ist aktiv und öffentlich sichtbar</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E3E8EC] bg-[#F7F9FA]">
              <button onClick={close} className="px-5 h-10 rounded-md border border-[#E3E8EC] text-[#1d1d1b] font-tech text-[11px] tracking-[0.18em] uppercase">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="machine-form-save"
                className="flex items-center gap-2 px-6 h-10 rounded-md bg-[#2C57D2] hover:bg-[#00AAA0] text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-60">
                {saving && <Loader2 size={13} className="animate-spin" />} Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
